<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  cleanLockedUsers,
  attemptFailed,
  lockInfoByUserId,
  ILockedUser,
} from '@/composables/lockedUsers';
import { useInstitutionStore } from '@/stores/institution';
import { useProfilesStore } from '@/stores/profiles.js';
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import i18n from '@/utils/i18n';
import { IInstitutionSettings, IProfile } from '@/interfaces';
import { useMatomo } from '@/composables/useMatomo';

const institutionStore = useInstitutionStore();
const profilesStore = useProfilesStore();
const baseOverlay = ref<InstanceType<typeof BaseOverlay> | null>(null);

const emit = defineEmits(['close', 'success', 'switch-user']);

interface Props {
  title: string;
  profile: IProfile;
  titleBold?: string;
  userSpecific?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  userSpecific: false,
});

const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
const colors = [
  '#DF2121',
  '#2BB914',
  '#2134DF',
  '#DFB521',
  '#DC0BC7',
  '#E87B17',
  '#5E21DF',
  '#953600',
  '#1FA3B5',
  '#929292',
];

const pin = ref([]);
const defaultLockInfo: ILockedUser = {
  id: '',
  pinLocked: false,
  pinAttempts: 0,
  timestamp: 0,
  duration: -1,
};

const lockedUserInfo = ref<ILockedUser>(defaultLockInfo);
const countDownInterval = ref<ReturnType<typeof setInterval>>();
const validPin = ref<any>(true);
const { trackEvent } = useMatomo();

const usePasswordProtection = computed(() => {
  return institutionStore.settings?.passwordProtection?.isProtected;
});

const pinValidation = computed((): any => profilesStore.pinValidation);

const displayedCountDown = computed(() => {
  return Math.ceil(lockedUserInfo.value?.duration / 60);
});

onMounted(() => {
  if (!usePasswordProtection.value) return;

  cleanLockedUsers();
  lockedUserInfo.value = lockInfoByUserId(props.profile.id) || defaultLockInfo;

  countDownInterval.value = setInterval(() => {
    cleanLockedUsers();
    lockedUserInfo.value =
      lockInfoByUserId(props.profile.id) || defaultLockInfo;
  }, 1000);

  trackEvent('View', 'EnterPin');
});

onUnmounted(() => {
  clearInterval(countDownInterval.value);
  profilesStore.setPinValidation({});
});

const infoMessages = computed(() => {
  return [
    {
      name: 'lockedPin',
      hasError: lockedUserInfo.value.pinLocked,

      title: i18n.global.t('modals.tooManyAttempts'),
      description: i18n.global.t('modals.tooManyAttemptsTryAgain', {
        minutes: displayedCountDown.value,
      }),
      type: 'error',
    },
    {
      name: 'validatingPin',
      hasError: pinValidation.value.validating,

      title: i18n.global.t('global.loading'),
      type: 'info',
    },
    {
      name: 'wrongPin',
      hasError:
        !pinValidation.value.validating &&
        pinValidation.value.validPin === false,

      title: i18n.global.t('modals.wrongPassword'),
      type: 'error',
    },
  ];
});

const hasInfoMessage = computed(() => {
  return Object.values(infoMessages.value).some((error) => error.hasError);
});

async function numberPressed(number: never) {
  if (pinValidation.value.validating || pinValidation.value.validPin === false)
    return;

  pin.value.push(number);

  if (pin.value.length < 4) return;

  validPin.value = await profilesStore.validatePin({
    profile: props.profile,
    pin: pin.value.join(''),
  });

  if (!pinValidation.value.validPin) {
    if (usePasswordProtection.value) {
      attemptFailed(
        props.profile.id,
        institutionStore.settings ?? ({} as IInstitutionSettings)
      );
    }
    resetPin();
    return;
  }

  emit('success', pin.value.join(''));
}

function resetPin() {
  pin.value = [];
  setTimeout(() => {
    profilesStore.setPinValidation({});
  }, 2000);
}

function switchUser() {
  baseOverlay.value?.close();
  emit('switch-user');
}
</script>

<template>
  <BaseOverlay
    ref="baseOverlay"
    :header-image="profile.displayImage"
    :scroll-height="600"
    @close="emit('close')"
  >
    <template #content>
      <div class="enter-pin">
        <div class="flex flex-col items-center relative">
          <div class="h1000 text-center mb-8">
            {{ profile.displayName }}
          </div>

          <div
            v-if="hasInfoMessage"
            v-for="message in infoMessages"
            :key="message.name"
          >
            <div
              v-if="message.hasError"
              class="mb-4 flex flex-col items-center"
            >
              <p
                class="b500"
                :class="{ 'text-red-400': message.type === 'error' }"
              >
                {{ message.title }}
              </p>

              <p
                v-if="message.description"
                class="b500"
                :class="{ 'text-red-400': message.type === 'error' }"
              >
                {{ message.description }}
              </p>
            </div>
          </div>

          <p class="b500">
            {{ title }} <span class="font-bold">{{ titleBold }}</span>
          </p>

          <div
            class="flex flex-col flex-1 items-center mt-10"
            :class="{ validating: pinValidation.validating }"
          >
            <div v-if="!lockedUserInfo.pinLocked" class="inline-flex mb-10">
              <div
                v-for="(item, index) in 4"
                :key="index"
                class="w-14 h-14 flex items-center justify-center"
                :class="{ 'mr-3': index !== 4 }"
              >
                <div
                  class="rounded-full"
                  :class="{
                    'bg-neutral-300': pin.length < item,
                    'bg-neutral-0 border-2 border-neutral-400':
                      pin.length >= item,
                    'bg-red-100 border-2 border-red-400 w-8 h-8':
                      pinValidation.validPin === false &&
                      !pinValidation.validating,
                    'w-14 h-14':
                      pinValidation.validPin !== false ||
                      pinValidation.validating,
                  }"
                ></div>
              </div>

              <fa-icon
                v-if="pinValidation.validPin !== false"
                class="text-3xl self-center"
                :icon="['fal', 'backspace']"
                @click="pin.pop()"
              />
            </div>

            <div
              v-if="!lockedUserInfo.pinLocked"
              class="flex flex-wrap mb-10 justify-center w-1/3"
            >
              <div
                v-for="(number, index) in numbers"
                :key="index"
                class="w-20 h-20 shadow-lg rounded-full mb-3 text-5xl flex items-center justify-center text-white"
                :class="{ 'mr-3': ![3, 6, 9, 0].includes(number) }"
                :style="{ backgroundColor: colors[index] }"
                @click="numberPressed(number as never)"
              >
                {{ number }}
              </div>
            </div>
          </div>

          <div class="mt-12 flex justify-center" v-if="!userSpecific">
            <div
              class="rounded-full bg-white -mt-10 py-6 px-24 flex items-center justify-center shadow-lg absolute"
            >
              <fa-icon class="text-3xl mr-3" :icon="['far', 'sign-out']" />

              <p class="font-bold text-2xl" @click="switchUser">
                {{ $t('global.switchUser') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <slot name="actions"> </slot>
  </BaseOverlay>
</template>

<style lang="scss" scoped>
.enter-pin {
  .validating {
    opacity: 0.5;
  }
}
</style>
